import React, { useState } from 'react';
import { Rocket, BookOpen, Target, Award, CheckCircle, Star, Zap, Brain, TrendingUp, Users } from 'lucide-react';

const BeginnerCourses = () => {
  const [selectedGrade, setSelectedGrade] = useState('all');

  const courses = [
    {
      title: "Foundation Mathematics",
      grade: "Class 6-8",
      category: "foundation",
      description: "Build strong mathematical foundation with focus on arithmetic, algebra basics, geometry, and logical reasoning",
      modules: ["Number Systems", "Basic Algebra", "Geometry Fundamentals", "Mensuration", "Data Handling"],
      duration: "6 Months",
      level: "Beginner"
    },
    {
      title: "Foundation Science",
      grade: "Class 6-8",
      category: "foundation",
      description: "Introduction to Physics, Chemistry, and Biology with hands-on experiments and real-world applications",
      modules: ["Matter & Materials", "Force & Motion", "Living Organisms", "Light & Sound", "Simple Machines"],
      duration: "6 Months",
      level: "Beginner"
    },
    {
      title: "Pre-JEE Foundation",
      grade: "Class 8-9",
      category: "competitive",
      description: "Early preparation for IIT-JEE with introduction to advanced concepts in Physics, Chemistry, and Mathematics",
      modules: ["Advanced Math Concepts", "Physics Fundamentals", "Chemical Reactions", "Problem Solving", "Logical Thinking"],
      duration: "12 Months",
      level: "Beginner"
    },
    {
      title: "NEET Foundation",
      grade: "Class 9-10",
      category: "competitive",
      description: "Medical entrance foundation covering Biology, Physics, and Chemistry with NCERT focus",
      modules: ["Cell Biology", "Human Body Systems", "Chemical Bonding", "Motion & Forces", "Plant Kingdom"],
      duration: "12 Months",
      level: "Beginner"
    },
    {
      title: "Olympiad Preparation",
      grade: "Class 6-10",
      category: "competitive",
      description: "Prepare for national and international olympiads in Mathematics, Science, and Cyber with problem-solving focus",
      modules: ["Advanced Problem Solving", "Logical Reasoning", "Scientific Thinking", "Quick Calculations", "Pattern Recognition"],
      duration: "8 Months",
      level: "Beginner to Intermediate"
    },
    {
      title: "English Communication",
      grade: "Class 6-10",
      category: "skills",
      description: "Improve grammar, vocabulary, reading comprehension, and writing skills for academic excellence",
      modules: ["Grammar Mastery", "Vocabulary Building", "Essay Writing", "Comprehension Skills", "Public Speaking"],
      duration: "6 Months",
      level: "Beginner"
    },
    {
      title: "Coding for Kids",
      grade: "Class 6-10",
      category: "skills",
      description: "Introduction to programming with Scratch, Python basics, and computational thinking",
      modules: ["Scratch Programming", "Python Basics", "Algorithm Thinking", "Problem Decomposition", "Project Building"],
      duration: "8 Months",
      level: "Beginner"
    },
    {
      title: "Vedic Mathematics",
      grade: "Class 6-10",
      category: "skills",
      description: "Learn ancient Indian mathematical techniques for fast calculations and mental math",
      modules: ["Speed Calculations", "Mental Math Tricks", "Squaring & Cubing", "Division Techniques", "Algebra Shortcuts"],
      duration: "4 Months",
      level: "Beginner"
    }
  ];

  const features = [
    {
      icon: Brain,
      title: "Concept Building",
      description: "Focus on understanding fundamentals rather than rote learning. Build strong conceptual foundation."
    },
    {
      icon: Zap,
      title: "Interactive Learning",
      description: "Engaging video lessons, animations, and interactive content make learning fun and effective."
    },
    {
      icon: Target,
      title: "Practice Problems",
      description: "Extensive problem sets with varying difficulty levels to reinforce learning and build confidence."
    },
    {
      icon: Award,
      title: "Progress Tracking",
      description: "Regular assessments, quizzes, and detailed progress reports to monitor improvement."
    }
  ];

  const whyStart = [
    {
      title: "Early Start Advantage",
      description: "Starting early gives students more time to build strong fundamentals, develop problem-solving skills, and stay ahead of the competition. Research shows students who begin competitive prep in middle school perform significantly better."
    },
    {
      title: "Build Strong Foundation",
      description: "Our beginner courses focus on conceptual clarity and fundamental understanding. We ensure students grasp the 'why' behind every concept, not just the 'how'. This deep understanding forms the bedrock for advanced learning."
    },
    {
      title: "Develop Learning Habits",
      description: "Early exposure to structured learning helps students develop discipline, time management, and study habits that last a lifetime. These skills are as important as subject knowledge for long-term success."
    },
    {
      title: "Reduce Future Pressure",
      description: "By starting early, students can learn at a comfortable pace without the stress of cramming. This gradual progression makes advanced topics in higher classes much more manageable and less overwhelming."
    }
  ];

  const pathways = [
    {
      title: "IIT-JEE Pathway",
      grades: ["Class 6-7: Foundation Math & Science", "Class 8-9: Pre-JEE Foundation", "Class 10: Advanced Concepts", "Class 11-12: JEE Main & Advanced"],
      color: "#58A95B"
    },
    {
      title: "NEET Pathway",
      grades: ["Class 6-8: Science Foundation", "Class 9-10: Biology Focus + PCM", "Class 11-12: NEET Intensive", "Droppers: Targeted Preparation"],
      color: "#58A95B"
    },
    {
      title: "Board Excellence",
      grades: ["Class 6-8: Strong Fundamentals", "Class 9-10: Board Prep + Concepts", "Class 11-12: Advanced Board Prep", "Competitive Exams Ready"],
      color: "#58A95B"
    }
  ];

  const filteredCourses = selectedGrade === 'all' ? courses : courses.filter(c => c.category === selectedGrade);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', backgroundColor: '#fff' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        color: 'white',
        padding: '100px 20px 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 70% 30%, rgba(88, 169, 91, 0.15) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite'
        }} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center' }}>
            <div>
              <h1 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.8s ease-out',
                lineHeight: '1.2'
              }}>
                Beginner Courses
                <span style={{ color: '#58A95B', display: 'block' }}>Start Your Journey Right</span>
              </h1>
              <p style={{
                fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                marginBottom: '2rem',
                lineHeight: '1.8',
                animation: 'fadeInUp 1s ease-out'
              }}>
                Perfect for students in Classes 6-10 to build strong fundamentals and prepare for competitive exams. 
                Learn from IIT alumni with courses designed to make concepts crystal clear and learning enjoyable.
              </p>
              <button style={{
                backgroundColor: '#58A95B',
                border: 'none',
                padding: '15px 40px',
                fontSize: '1.1rem',
                fontWeight: '600',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                animation: 'fadeInUp 1.2s ease-out'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}>
                Explore Courses
              </button>
            </div>
            <div style={{ textAlign: 'center', animation: 'float 3s ease-in-out infinite' }}>
              <Rocket size={200} color="#58A95B" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </section>

      {/* Why Start Early */}
      <section style={{ padding: '80px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#000000'
          }}>
            Why Start <span style={{ color: '#58A95B' }}>Early?</span>
          </h2>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: '#666', marginBottom: '3rem', maxWidth: '800px', margin: '0 auto 3rem' }}>
            The journey to IIT, NEET, or academic excellence begins much before Class 11
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '30px'
          }}>
            {whyStart.map((item, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                borderRadius: '15px',
                padding: '30px',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                borderLeft: '4px solid #58A95B'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(88, 169, 91, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '15px', color: '#000' }}>{item.title}</h4>
                <p style={{ color: '#666', lineHeight: '1.7', margin: 0 }}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '80px 20px', backgroundColor: '#ffffff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem',
            color: '#000000'
          }}>
            What Makes Our <span style={{ color: '#58A95B' }}>Courses Special?</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}>
            {features.map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <div key={idx} style={{
                  textAlign: 'center',
                  padding: '30px 20px',
                  borderRadius: '15px',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <IconComponent size={50} color="#58A95B" style={{ marginBottom: '20px' }} />
                  <h5 style={{ fontWeight: '600', marginBottom: '10px', color: '#000' }}>{feature.title}</h5>
                  <p style={{ color: '#666', lineHeight: '1.6', margin: 0 }}>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Course Filter */}
      <section style={{ padding: '80px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '2rem',
            color: '#000000'
          }}>
            Browse <span style={{ color: '#58A95B' }}>Courses</span>
          </h2>
          
          {/* Filter Buttons */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '15px',
            marginBottom: '3rem',
            flexWrap: 'wrap'
          }}>
            {[
              { value: 'all', label: 'All Courses' },
              { value: 'foundation', label: 'Foundation' },
              { value: 'competitive', label: 'Competitive Prep' },
              { value: 'skills', label: 'Skills Development' }
            ].map((filter) => (
              <button
                key={filter.value}
                onClick={() => setSelectedGrade(filter.value)}
                style={{
                  padding: '12px 30px',
                  borderRadius: '25px',
                  border: '2px solid',
                  borderColor: selectedGrade === filter.value ? '#58A95B' : '#ddd',
                  backgroundColor: selectedGrade === filter.value ? '#58A95B' : 'white',
                  color: selectedGrade === filter.value ? 'white' : '#333',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '1rem'
                }}
                onMouseEnter={(e) => {
                  if (selectedGrade !== filter.value) {
                    e.target.style.borderColor = '#58A95B';
                    e.target.style.color = '#58A95B';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedGrade !== filter.value) {
                    e.target.style.borderColor = '#ddd';
                    e.target.style.color = '#333';
                  }
                }}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Courses Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '30px'
          }}>
            {filteredCourses.map((course, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(88, 169, 91, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
                  padding: '25px 20px',
                  color: 'white',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    padding: '5px 15px',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: '600'
                  }}>
                    {course.level}
                  </div>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '8px', paddingRight: '100px' }}>{course.title}</h4>
                  <p style={{ margin: 0, opacity: 0.95, fontSize: '0.95rem' }}>{course.grade}</p>
                </div>
                
                <div style={{ padding: '25px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <p style={{ color: '#333', marginBottom: '20px', lineHeight: '1.6' }}>
                    {course.description}
                  </p>
                  
                  <div style={{ marginBottom: '20px', flex: 1 }}>
                    <h6 style={{ fontWeight: '600', marginBottom: '12px', color: '#000', fontSize: '0.95rem' }}>
                      Course Modules:
                    </h6>
                    {course.modules.slice(0, 4).map((module, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <CheckCircle size={16} color="#58A95B" style={{ marginRight: '10px', marginTop: '2px', flexShrink: 0 }} />
                        <span style={{ color: '#666', fontSize: '0.9rem' }}>{module}</span>
                      </div>
                    ))}
                    {course.modules.length > 4 && (
                      <p style={{ color: '#58A95B', fontSize: '0.9rem', marginTop: '8px', marginBottom: 0 }}>
                        +{course.modules.length - 4} more modules
                      </p>
                    )}
                  </div>
                  
                  <div style={{
                    borderTop: '1px solid #eee',
                    paddingTop: '15px',
                    marginBottom: '15px',
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span><strong>Duration:</strong> {course.duration}</span>
                      <Star size={16} color="#58A95B" fill="#58A95B" />
                    </div>
                  </div>
                  
                  <button style={{
                    width: '100%',
                    backgroundColor: '#58A95B',
                    border: 'none',
                    padding: '12px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#4a8f4d'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#58A95B'}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Pathways */}
      <section style={{ padding: '80px 20px', backgroundColor: '#000000', color: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '1rem'
          }}>
            Learning <span style={{ color: '#58A95B' }}>Pathways</span>
          </h2>
          <p style={{ textAlign: 'center', fontSize: '1.1rem', color: '#ddd', marginBottom: '3rem' }}>
            Structured progression from foundation to advanced competitive preparation
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {pathways.map((pathway, idx) => (
              <div key={idx} style={{
                backgroundColor: '#1a1a1a',
                borderRadius: '15px',
                padding: '30px',
                border: '2px solid #333',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#58A95B';
                e.currentTarget.style.transform = 'translateY(-5px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#333';
                e.currentTarget.style.transform = 'translateY(0)';
              }}>
                <h4 style={{ fontWeight: 'bold', marginBottom: '20px', color: '#58A95B', fontSize: '1.3rem' }}>
                  {pathway.title}
                </h4>
                {pathway.grades.map((grade, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    marginBottom: '15px',
                    paddingLeft: '10px',
                    borderLeft: '3px solid #58A95B'
                  }}>
                    <div style={{
                      width: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      backgroundColor: '#58A95B',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px',
                      flexShrink: 0,
                      fontWeight: 'bold',
                      fontSize: '0.9rem'
                    }}>
                      {i + 1}
                    </div>
                    <p style={{ color: '#ddd', margin: 0, lineHeight: '1.6' }}>{grade}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            Start Your Child's Success Story Today
          </h2>
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', marginBottom: '2rem', lineHeight: '1.6' }}>
            Give your child the advantage of early preparation. Enroll in our beginner courses and build a strong foundation for future success.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{
              backgroundColor: '#000000',
              border: 'none',
              padding: '15px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#1a1a1a';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#000000';
              e.target.style.transform = 'scale(1)';
            }}>
              Enroll Now
            </button>
            <button style={{
              backgroundColor: 'transparent',
              border: '2px solid white',
              padding: '15px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#58A95B';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'white';
            }}>
              Download Brochure
            </button>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 0.8;
          }
        }

        * {
          box-sizing: border-box;
        }

        button {
          font-family: inherit;
        }

        @media (max-width: 768px) {
          section {
            padding: 60px 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BeginnerCourses;